import { useTranslation } from 'next-i18next';
import { trackEvent } from '@zep/logger/tracking';
import { useLocale } from '@/hooks';
import { Button } from '@/ui_v3';
import { getSampleBoardUrl, getSampleImage, SECTION_PADDING } from '../constants';

/**
 * 시안 순서: 담벼락 → 그룹 → 타임라인.
 * `as const` 로 키를 리터럴로 고정한다 — `string` 으로 넓히면 시트에서 키가
 * 사라져도 컴파일이 통과해 버린다(ZEP-443 의 dead-key 사각지대).
 * boardType 도 리터럴로 좁혀져 getSampleImage·getSampleBoardUrl 의
 * BoardTypeValue 인자에 그대로 들어간다.
 */
const SAMPLES = [{
  boardType: 'WALL',
  titleKey: 'boardLanding:samples.wall.title',
  descriptionKey: 'boardLanding:samples.wall.description'
}, {
  boardType: 'GROUP',
  titleKey: 'boardLanding:samples.group.title',
  descriptionKey: 'boardLanding:samples.group.description'
}, {
  boardType: 'TIMELINE',
  titleKey: 'boardLanding:samples.timeline.title',
  descriptionKey: 'boardLanding:samples.timeline.description'
}] as const;
export function BoardLandingSamples() {
  const {
    locale
  } = useLocale();
  const {
    t
  } = useTranslation(['boardLanding']);
  return (
    // 이 섹션만 배경이 연한 파랑이다(Figma color/blue/50 = #e7f4ff). ZUS 의 시맨틱
    // bg 매핑(neutral·alternative)은 전부 gray 계열이라 대응 클래스가 없어 값을 직접 쓴다.
    // 흰 카드를 이 배경에서 띄우는 게 이 섹션의 구성이라 카드에 테두리와 그림자가 붙는다.
    <section aria-labelledby="board-landing-samples-heading" className={`flex flex-col items-center gap-2xl bg-[#e7f4ff] pb-3xl pt-3xl min-[751px]:py-2xl ${SECTION_PADDING}`} data-sentry-component="BoardLandingSamples" data-sentry-source-file="BoardLandingSamples.tsx">
      {/*
        Intro 와 달리 Samples 는 Figma 모바일 프레임(25561:22588, "pricing - title")
        에서 제목 줄높이가 이미 display-sm-extrabold 다운스케일(30px)과 일치한다
        (heading-xl-extrabold 다운스케일 24px 이 아니다) — 1025px 업그레이드 없이
        전 구간 display-sm-extrabold 고정이 맞다.
       */}
      <h2 id="board-landing-samples-heading" className="text-center text-display-sm-extrabold text-default">
        {t('boardLanding:samples.title')}
      </h2>

      {/* items-stretch 로 3장의 높이를 맞춘다 — 설명 줄 수가 달라도 버튼이 같은 선에 온다. */}
      <ul className="grid w-full max-w-[1280px] grid-cols-1 items-stretch justify-items-center gap-lg min-[1025px]:grid-cols-3">
        {SAMPLES.map(sample => {
          const title = t(sample.titleKey);
          return <li key={sample.boardType} className="flex size-full max-w-[400px] flex-col gap-sm overflow-hidden rounded-[24px] border border-[#e9eaf2] bg-default shadow-[0_4px_8px_4px_rgba(28,27,31,0.04)] min-[751px]:gap-lg">
              <div className="px-sm pb-3xs pt-sm min-[751px]:px-lg min-[751px]:pb-xs min-[751px]:pt-lg">
                <img src={getSampleImage(locale, sample.boardType)} alt={t('boardLanding:samples.imageAlt', {
                title
              })} width={376} height={241} loading="lazy" className="aspect-[824/528] w-full rounded-sm border border-[#f0f2f6] object-cover" />
              </div>

              <div className="flex flex-1 flex-col gap-xs px-sm min-[751px]:gap-md min-[751px]:px-xl">
                <h3 className="text-heading-lg-extrabold text-default opacity-90">
                  {title}
                </h3>
                <p className="text-body-lg-semibold text-alternative">
                  {t(sample.descriptionKey)}
                </p>
              </div>

              <div className="px-sm pb-sm pt-3xs min-[751px]:px-xl min-[751px]:pb-xl min-[751px]:pt-xs">
                <Button variant="contained" intent="strong" size="lg" rounded="default" className="w-full" asChild>
                  {/*
                    prod 보드 절대 URL + 새 창. 내부 라우팅으로 보내면 dev/preview 에서
                    404 가 되고, 랜딩을 떠나보내는 링크라 새 창이 맞다.
                    외부 origin 이라 next/link 의 prefetch 이득도 없다.
                   */}
                  <a href={getSampleBoardUrl(locale, sample.boardType)} target="_blank" rel="noopener noreferrer" aria-label={t('boardLanding:samples.linkLabel', {
                  title
                })} data-testid={`board-landing-sample-${sample.boardType}`} onClick={() => trackEvent('board_landing_sample_click', {
                  board_type: sample.boardType
                })}>
                    {t('boardLanding:samples.viewButton')}
                  </a>
                </Button>
              </div>
            </li>;
        })}
      </ul>
    </section>
  );
}